import cornerstone from 'cornerstone-core';
import dicomParser from 'dicom-parser';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import { deriveStudyTags } from './imagingTags';
import { jpeg2000ImageLoader } from './jpeg2000ImageLoader';

cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

// Saved series thumbnails: large enough for crisp display on retina and in
// hover previews (~360px CSS), without oversized uploads.
const STUDY_THUMBNAIL_PIXEL_SIZE = 512;
const STUDY_THUMBNAIL_JPEG_QUALITY = 0.92;

const UNCOMPRESSED_TRANSFER_SYNTAXES = new Set([
    '1.2.840.10008.1.2',
    '1.2.840.10008.1.2.1',
    '1.2.840.10008.1.2.2',
]);

export const dicomHelpers = {
    hashFile: async (file) => {
        const buffer = await file.arrayBuffer();
        const digest = await crypto.subtle.digest('SHA-512', buffer);
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    },
    displayDate: (isoDate) => {
        if (!isoDate || typeof isoDate !== 'string') {
            return '';
        }

        // Expect ISO 8601: YYYY-MM-DDTHH:mm:ssZ (or just YYYY-MM-DD)
        const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) {
            return '';
        }

        const [, year, month, day] = match;

        const m = parseInt(month, 10);
        const d = parseInt(day, 10);
        if (m < 1 || m > 12 || d < 1 || d > 31) {
            return '';
        }

        return `${month}/${day}/${year}`;
    },
    dicomDateToISO: (dicomDate) => {
        if (!dicomDate || typeof dicomDate !== 'string') return null;
        const cleaned = dicomDate.trim().replace(/[.\-/]/g, '');
        if (!/^\d{8}$/.test(cleaned)) return null;
        const year = cleaned.slice(0, 4);
        const month = cleaned.slice(4, 6);
        const day = cleaned.slice(6, 8);
        return `${year}-${month}-${day}T00:00:00Z`;
    },
    getSourceFileKey: (file) => {
        return file.webkitRelativePath || file.name;
    },
    renderSliceToBlob: async (slice, { width = 128, height = 128, quality = 0.85 } = {}) => {
        const element = document.createElement('div');
        element.style.width = `${width}px`;
        element.style.height = `${height}px`;
        element.style.position = 'absolute';
        element.style.left = '-9999px';
        element.style.top = '-9999px';
        document.body.appendChild(element);

        let enabled = false;
        try {
            cornerstone.enable(element);
            enabled = true;

            const image = await cornerstone.loadImage(slice.imageId);
            const viewport = cornerstone.getDefaultViewportForImage(element, image);
            cornerstone.displayImage(element, image, viewport);

            await new Promise((resolve) => {
                const onRendered = () => {
                    element.removeEventListener('cornerstoneimagerendered', onRendered);
                    resolve();
                };
                element.addEventListener('cornerstoneimagerendered', onRendered);
                cornerstone.updateImage(element, true);
            });

            const enabledEl = cornerstone.getEnabledElement(element);
            const canvas = enabledEl && enabledEl.canvas;
            if (!canvas) return null;

            const blob = await new Promise((resolve) => {
                canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
            });
            return blob;
        } catch (err) {
            console.log('renderSliceToBlob error:', err);
            return null;
        } finally {
            if (enabled) {
                try { cornerstone.disable(element); } catch (e) { /* ignore */ }
            }
            if (element.parentNode) element.parentNode.removeChild(element);
        }
    },
    // TODO: REVISIT — the object URLs minted below are never revoked, so
    // every thumbnail leaks its blob for the lifetime of the tab. Needs a
    // revokeObjectURL when a study leaves the list (save, delete, unmount).
    //
    // TODO: REVISIT — a null blob from renderSliceToBlob is swallowed here,
    // leaving the series with no thumbnailHash and no way to retry. See the
    // save-flow audit note in UploadImages.handleSave.
    generateStudyThumbnails: async (studies, onProgress) => {
        for (const study of studies) {
            for (const seriesEntry of study.series) {
                const blob = await dicomHelpers.renderSliceToBlob(seriesEntry.middleSlice, {
                    width: STUDY_THUMBNAIL_PIXEL_SIZE,
                    height: STUDY_THUMBNAIL_PIXEL_SIZE,
                    quality: STUDY_THUMBNAIL_JPEG_QUALITY,
                });
                seriesEntry.thumbnailBlob = blob;
                seriesEntry.thumbnailUrl = blob ? URL.createObjectURL(blob) : null;
                seriesEntry.thumbnailHash = blob ? await dicomHelpers.hashFile(blob) : null;
                if (onProgress) onProgress();
            }
        }
        return studies;
    },
    getPerFrameMetadata: (dataSet, frameIndex) => {
        const result = {
            sliceLocation: undefined,
            imagePosition: null,
            xPosition: 0,
            yPosition: 0,
            zPosition: 0
        };

        try {
            const perFrameSeq = dataSet.elements.x52009230;
            if (perFrameSeq && perFrameSeq.items && perFrameSeq.items[frameIndex]) {
                const frameItem = perFrameSeq.items[frameIndex].dataSet;
                const planePosSeq = frameItem.elements.x00209113;

                if (planePosSeq && planePosSeq.items && planePosSeq.items[0]) {
                    const planePosItem = planePosSeq.items[0].dataSet;
                    const imagePosition = planePosItem.string('x00200032');
                    if (imagePosition) {
                        result.imagePosition = imagePosition;
                        const positions = imagePosition.split('\\');
                        result.xPosition = parseFloat(positions[0]) || 0;
                        result.yPosition = parseFloat(positions[1]) || 0;
                        result.zPosition = parseFloat(positions[2]) || 0;
                    }
                }
            }
        } catch (err) {

        }

        return result;
    },

    // TODO: REVISIT — fileManager.add() below registers the File with
    // cornerstone's global wadouri file manager and nothing ever calls
    // fileManager.purge(). Every file parsed in the session stays
    // referenced for the lifetime of the tab, so repeated folder loads and
    // repeated series hydrations on ImagesPage accumulate indefinitely.
    // Needs a purge (or per-file remove) once the imageIds for a study are
    // no longer displayable.
    extractImageDataFromFile: async (file) => {
        const dataSet = dicomParser.parseDicom(new Uint8Array(await file.arrayBuffer()));
        if (dataSet.elements.x7fe00010?.encapsulatedPixelData
            && UNCOMPRESSED_TRANSFER_SYNTAXES.has(dataSet.string('x00020010'))) {
            throw new Error(`${file.name}: pixel data is compressed but the header says uncompressed`);
        }
        const fileHash = await dicomHelpers.hashFile(file);
        let imageData = []
        let baseImageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(file);
        let firstImage;
        try {
            firstImage = await cornerstone.loadImage(baseImageId);
        } catch (wadoError) {
            if (!jpeg2000ImageLoader.isJpeg2000DataSet(dataSet)) {
                throw wadoError;
            }
            baseImageId = jpeg2000ImageLoader.addFile(file, dataSet);
            firstImage = await cornerstone.loadImage(baseImageId);
        }


        const parseIntSafe = (value, fallback = null) => {
            const parsed = parseInt(value, 10);
            return isNaN(parsed) ? fallback : parsed;
        };

        // Patient
        const patientName = firstImage.data.string('x00100010');
        const patientID = firstImage.data.string('x00100020');
        const patientBirthDate = firstImage.data.string('x00100030');
        const patientSex = firstImage.data.string('x00100040');

        // Study
        const studyInstanceUID = firstImage.data.string('x0020000d');
        const description = firstImage.data.string('x00081030');
        const studyDate = dicomHelpers.dicomDateToISO(firstImage.data.string('x00080020'));
        const studyTime = firstImage.data.string('x00080030');

        // Series
        const seriesInstanceUID = firstImage.data.string('x0020000e');
        const seriesNumber = firstImage.data.intString('x00200011');
        const seriesDescription = firstImage.data.string('x0008103e');
        const bodyPartExamined = firstImage.data.string('x00180015');
        const laterality = firstImage.data.string('x00200060') || firstImage.data.string('x00200062');
        const numberOfFrames = parseIntSafe(firstImage.data.intString('x00280008'), 1);

        // Modality
        const modality = firstImage.data.string('x00080060');
        const manufacturer = firstImage.data.string('x00080070');
        const manufacturerModelName = firstImage.data.string('x00081090');
        const protocolName = firstImage.data.string('x00181030');

        const sourceFileKey = dicomHelpers.getSourceFileKey(file);
        const acquisitionMeta = { modality, laterality, protocolName };

        console.log({
            studyInstanceUID,
            numberOfFrames,
        })
        if (numberOfFrames > 1) {
            for (let frame = 0; frame < numberOfFrames; frame += 1) {
                const frameImageId = `${baseImageId}?frame=${frame}`;
                const frameMeta = dicomHelpers.getPerFrameMetadata(firstImage.data, frame);
                const sortValue = frameMeta.imagePosition ? frameMeta.zPosition : frame;


                imageData.push({
                    sourceFile: file,
                    imageId: frameImageId,
                    fileName: `${file.name} [frame ${frame + 1}/${numberOfFrames}]`,
                    instanceNumber: frame + 1,
                    sliceLocation: frameMeta.sliceLocation,
                    imagePosition: frameMeta.imagePosition,
                    seriesDescription,
                    seriesInstanceUID,
                    seriesNumber: seriesNumber ? parseInt(seriesNumber, 10) : 0,
                    studyInstanceUID,
                    description,
                    studyDate,
                    bodyPartExamined,
                    ...acquisitionMeta,
                    sortValue,
                    xPosition: frameMeta.xPosition,
                    yPosition: frameMeta.yPosition,
                    zPosition: frameMeta.zPosition,
                    isMultiFrame: true,
                    frameIndex: frame,
                    sourceFileKey,
                    sourceFileHash: fileHash
                });
            }
        } else {
            const instanceNumber = firstImage.data.intString('x00200013');
            const sliceLocation = firstImage.data.floatString('x00201041');
            const imagePosition = firstImage.data.string('x00200032');
            const imageOrientation = firstImage.data.string('x00200037');
            let xPosition = 0;
            let yPosition = 0;
            let zPosition = 0;

            if (imagePosition) {
                const positions = imagePosition.split('\\');
                xPosition = parseFloat(positions[0]) || 0;
                yPosition = parseFloat(positions[1]) || 0;
                zPosition = parseFloat(positions[2]) || 0;
            }

            const sortValue = sliceLocation !== undefined ? parseFloat(sliceLocation) : zPosition;

            imageData.push({
                sourceFile: file,
                imageId: baseImageId,
                fileName: file.name,
                instanceNumber: instanceNumber ? parseInt(instanceNumber, 10) : 0,
                sliceLocation,
                imagePosition,
                imageOrientation,
                seriesDescription,
                seriesInstanceUID,
                seriesNumber: seriesNumber ? parseInt(seriesNumber, 10) : 0,
                studyInstanceUID,
                description,
                studyDate,
                bodyPartExamined,
                ...acquisitionMeta,
                sortValue,
                xPosition,
                yPosition,
                zPosition,
                isMultiFrame: false,
                frameIndex: 0,
                sourceFileKey,
                sourceFileHash: fileHash
            });
        }
        return imageData
    },
sortSeriesSlices: (seriesItems) => {
    // Multi-frame fallback: if positions are missing, frameIndex is the
    // only signal we have.
    const allMultiFrameNoPosition = seriesItems.every(
        (s) => s.isMultiFrame && !s.imagePosition
    );
    if (allMultiFrameNoPosition) {
        return [...seriesItems].sort((a, b) => a.frameIndex - b.frameIndex);
    }

    // Detect a localizer/scout-style series: slices don't form a single
    // contiguous stack along one axis. We check by counting how many
    // distinct "tracks" the slices fall into, where a track is a group
    // of slices sharing roughly the same (x, y) — i.e. stepping along z —
    // or the same (x, z), or the same (y, z).
    //
    // For a normal volumetric series, ONE of these groupings will collapse
    // every slice into a single bucket. For a mixed localizer series,
    // none of them will.
    const EPSILON = 0.5; // mm; loose enough for floating-point jitter
    const bucketKey = (val) => Math.round(val / EPSILON);

    const groupCounts = (keyFn) => {
        const groups = new Set();
        seriesItems.forEach((s) => groups.add(keyFn(s)));
        return groups.size;
    };

    const xyGroups = groupCounts((s) => `${bucketKey(s.xPosition)},${bucketKey(s.yPosition)}`);
    const xzGroups = groupCounts((s) => `${bucketKey(s.xPosition)},${bucketKey(s.zPosition)}`);
    const yzGroups = groupCounts((s) => `${bucketKey(s.yPosition)},${bucketKey(s.zPosition)}`);

    // If at least one axis-pair collapses everything into a single group
    // (or very few groups relative to slice count), it's a real stack and
    // we can sort spatially. Otherwise, it's a mixed-localizer series and
    // InstanceNumber is the safest ordering.
    //
    // TODO: REVISIT — this heuristic has a known false positive on oblique
    // acquisitions.
    //
    // The check below assumes a genuine stack holds two of the three
    // coordinates fixed while stepping along the third. That is true for
    // pure axial (x, y fixed; z steps), sagittal, and coronal series. It is
    // NOT true for an oblique acquisition, where the slice normal is not
    // aligned to a patient axis, so x, y and z all change on every slice.
    // Oblique planning is routine in MRI (oblique coronal knee, angled
    // lumbar disc spaces, oblique axial shoulder).
    //
    // For those series all three groupings return one bucket per slice,
    // minGroups equals the slice count, and any series of 3+ slices is
    // misclassified as a localizer — so we silently drop to InstanceNumber
    // ordering in exactly the case where spatial ordering matters most.
    // InstanceNumber usually happens to agree with spatial order, which is
    // why this has not obviously broken anything yet, but it will not agree
    // for multi-echo series or PACS exports that renumbered instances.
    //
    // The correct fix is to stop testing axis-aligned pairs and instead
    // project each ImagePositionPatient onto the slice normal, which is the
    // cross product of the two direction cosine vectors in
    // ImageOrientationPatient (tag 0020,0037 — not currently parsed in
    // extractImageDataFromFile, so that needs to be captured first). A real
    // stack is then monotonic along that single projected axis regardless
    // of how it is angled, and a mixed localizer is not.
    //
    // Two smaller issues to clean up at the same time:
    //   - bucketKey rounds rather than clusters, so two positions 0.4mm
    //     apart can still land either side of a boundary. The effective
    //     tolerance is not the 0.5mm that EPSILON implies.
    //   - the console.log in the fallback branch below should come out.
    const minGroups = Math.min(xyGroups, xzGroups, yzGroups);
    const looksLikeMixedLocalizer = minGroups > Math.max(2, seriesItems.length / 3);

    if (looksLikeMixedLocalizer) {
        console.log(
            `[sortSeriesSlices] Mixed-axis series detected ` +
            `(xy=${xyGroups}, xz=${xzGroups}, yz=${yzGroups} groups across ` +
            `${seriesItems.length} slices). Falling back to InstanceNumber.`
        );
        return [...seriesItems].sort((a, b) => {
            const ai = a.instanceNumber || 0;
            const bi = b.instanceNumber || 0;
            if (ai !== bi) return ai - bi;
            return a.frameIndex - b.frameIndex;
        });
    }

    // Normal volumetric path: spatial sort, with InstanceNumber as the
    // tiebreaker (was xPosition/yPosition before, but those are usually
    // identical across slices of a real axial stack and gave no useful
    // signal).
    return [...seriesItems].sort((a, b) => {
        if (Math.abs(a.sortValue - b.sortValue) > 0.01) {
            return a.sortValue - b.sortValue;
        }
        const ai = a.instanceNumber || 0;
        const bi = b.instanceNumber || 0;
        if (ai !== bi) return ai - bi;
        if (Math.abs(a.xPosition - b.xPosition) > 0.01) {
            return a.xPosition - b.xPosition;
        }
        return a.yPosition - b.yPosition;
    });
},
parseDicomVector: (value) => {
    if (!value) return null;
    const parts = String(value).split('\\').map((part) => parseFloat(part));
    return parts.every((part) => Number.isFinite(part)) ? parts : null;
},
splitSeriesIntoStacks: (seriesItems) => {
    const ORIENTATION_MATCH_THRESHOLD = 0.999;
    const canSplit = seriesItems.length > 1 && seriesItems.every((s) =>
        !s.isMultiFrame
        && dicomHelpers.parseDicomVector(s.imageOrientation)?.length === 6
        && dicomHelpers.parseDicomVector(s.imagePosition)?.length === 3
    );
    if (!canSplit) return [seriesItems];

    const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    const stacks = [];
    seriesItems.forEach((item) => {
        const orientation = dicomHelpers.parseDicomVector(item.imageOrientation);
        const row = orientation.slice(0, 3);
        const column = orientation.slice(3, 6);
        const stack = stacks.find((candidate) =>
            dot(candidate.row, row) > ORIENTATION_MATCH_THRESHOLD
            && dot(candidate.column, column) > ORIENTATION_MATCH_THRESHOLD
        );
        if (stack) {
            stack.items.push(item);
        } else {
            stacks.push({ row, column, items: [item] });
        }
    });
    if (stacks.length === 1) return [seriesItems];

    const firstInstance = (items) => Math.min(...items.map((s) => s.instanceNumber || 0));
    return stacks
        .map((stack) => stack.items)
        .sort((a, b) => firstInstance(a) - firstInstance(b));
},
sortStackAlongNormal: (stackItems) => {
    const orientation = dicomHelpers.parseDicomVector(stackItems[0].imageOrientation);
    const [rx, ry, rz, cx, cy, cz] = orientation;
    const normal = [ry * cz - rz * cy, rz * cx - rx * cz, rx * cy - ry * cx];
    const withDistance = stackItems.map((item) => {
        const [px, py, pz] = dicomHelpers.parseDicomVector(item.imagePosition);
        return { item, distance: px * normal[0] + py * normal[1] + pz * normal[2] };
    });

    const byInstance = [...withDistance].sort(
        (a, b) => (a.item.instanceNumber || 0) - (b.item.instanceNumber || 0)
    );
    const direction =
        byInstance[byInstance.length - 1].distance < byInstance[0].distance ? -1 : 1;

    return withDistance
        .sort((a, b) => {
            const delta = direction * (a.distance - b.distance);
            if (Math.abs(delta) > 0.01) return delta;
            return (a.item.instanceNumber || 0) - (b.item.instanceNumber || 0);
        })
        .map((entry) => entry.item);
},
buildStackSeriesKey: (seriesInstanceUID, stackIndex) => `${seriesInstanceUID}#${stackIndex}`,
getSeriesKey: (series) => series?.seriesKey || series?.seriesInstanceUID,
assignSeriesKeys: (seriesList) => {
    const stackCountByUID = {};
    seriesList.forEach((s) => {
        stackCountByUID[s.seriesInstanceUID] = (stackCountByUID[s.seriesInstanceUID] || 0) + 1;
    });
    const seenByUID = {};
    return seriesList.map((s) => {
        const stackCount = stackCountByUID[s.seriesInstanceUID];
        if (stackCount < 2) {
            return { ...s, seriesKey: s.seriesInstanceUID, stackIndex: 1, stackCount: 1 };
        }
        seenByUID[s.seriesInstanceUID] = (seenByUID[s.seriesInstanceUID] || 0) + 1;
        const stackIndex = seenByUID[s.seriesInstanceUID];
        return {
            ...s,
            seriesKey: dicomHelpers.buildStackSeriesKey(s.seriesInstanceUID, stackIndex),
            stackIndex,
            stackCount,
        };
    });
},
compareSeriesOrder: (a, b) => {
    const aNum = a?.seriesNumber || 0;
    const bNum = b?.seriesNumber || 0;
    if (aNum !== bNum) return aNum - bNum;
    return (a?.seriesDescription || '').localeCompare(b?.seriesDescription || '');
},
buildSeriesLabels: (seriesList) => {
    const describe = (s) =>
        s?.seriesDescription || s?.seriesInstanceUID || 'Unknown Series';

    const seriesUIDsByDesc = {};
    seriesList.forEach((s) => {
        const desc = describe(s);
        if (!seriesUIDsByDesc[desc]) seriesUIDsByDesc[desc] = new Set();
        seriesUIDsByDesc[desc].add(s.seriesInstanceUID);
    });

    const seen = {};
    const duplicateOrdinalByUID = {};
    const labels = {};
    [...seriesList].sort(dicomHelpers.compareSeriesOrder).forEach((s) => {
        const desc = describe(s);
        const numPrefix = s?.seriesNumber ? `S:${s.seriesNumber} ` : '';
        let label = `${numPrefix}${desc}`;
        if (seriesUIDsByDesc[desc].size > 1) {
            if (!(s.seriesInstanceUID in duplicateOrdinalByUID)) {
                seen[desc] = (seen[desc] || 0) + 1;
                duplicateOrdinalByUID[s.seriesInstanceUID] = seen[desc];
            }
            label += ` (${duplicateOrdinalByUID[s.seriesInstanceUID]})`;
        }
        if (s.stackCount > 1) {
            label += ` (${s.stackIndex}/${s.stackCount})`;
        }
        labels[dicomHelpers.getSeriesKey(s)] = label;
    });
    return labels;
},
extractStudiesFromImageData: (imageData) => {
    const bySeries = {};
    imageData.forEach((item) => {
        if (!bySeries[item.seriesInstanceUID]) {
            bySeries[item.seriesInstanceUID] = [];
        }
        bySeries[item.seriesInstanceUID].push(item);
    });

    // Step 2: bucket each series under its study.
    // `slicesByStudyAndSeries` is local scaffolding only — it is intentionally
    // not attached to the study object so it doesn't travel downstream.
    const grouped = {};
    const slicesByStudyAndSeries = {};
    const stacksByStudyAndSeriesKey = {};
    Object.keys(bySeries).forEach((seriesInstanceUID) => {
        const items = bySeries[seriesInstanceUID];
        const studyKey = items[0].studyInstanceUID || 'no-study-uid';
        if (!grouped[studyKey]) {
            grouped[studyKey] = {
                studyInstanceUID: studyKey,
                description: items[0].description || 'Unknown Study',
                studyDate: items[0].studyDate || '',
                labels: {}
            };
            slicesByStudyAndSeries[studyKey] = {};
            stacksByStudyAndSeriesKey[studyKey] = {};
        }
        const stacks = dicomHelpers.splitSeriesIntoStacks(items);
        if (stacks.length === 1) {
            slicesByStudyAndSeries[studyKey][seriesInstanceUID] = dicomHelpers.sortSeriesSlices(items);
            stacksByStudyAndSeriesKey[studyKey][seriesInstanceUID] = {
                seriesInstanceUID, stackIndex: 1, stackCount: 1
            };
            return;
        }
        stacks.forEach((stackItems, i) => {
            const seriesKey = dicomHelpers.buildStackSeriesKey(seriesInstanceUID, i + 1);
            slicesByStudyAndSeries[studyKey][seriesKey] = dicomHelpers.sortStackAlongNormal(stackItems);
            stacksByStudyAndSeriesKey[studyKey][seriesKey] = {
                seriesInstanceUID, stackIndex: i + 1, stackCount: stacks.length
            };
        });
    });

    Object.keys(grouped).forEach((studyKey) => {
        const study = grouped[studyKey];
        const seriesSlices = slicesByStudyAndSeries[studyKey];

        const descriptors = Object.keys(seriesSlices).map((sKey) => ({
            ...stacksByStudyAndSeriesKey[studyKey][sKey],
            seriesKey: sKey,
            seriesNumber: seriesSlices[sKey][0].seriesNumber || 0,
            seriesDescription: seriesSlices[sKey][0].seriesDescription,
            bodyPartExamined: seriesSlices[sKey][0].bodyPartExamined,
            modality: seriesSlices[sKey][0].modality,
            laterality: seriesSlices[sKey][0].laterality,
            protocolName: seriesSlices[sKey][0].protocolName,
        }));

        study.labels = dicomHelpers.buildSeriesLabels(descriptors);

        study.series = [...descriptors]
            .sort(dicomHelpers.compareSeriesOrder)
            .map((descriptor) => {
                const { seriesKey } = descriptor;
                const slices = seriesSlices[seriesKey] || [];
                if (!slices.length) return null;
                const middleIndex = Math.floor(slices.length / 2);
                const fileHashes = Array.from(
                    new Set(slices.map((s) => s.sourceFileHash).filter(Boolean))
                );
                return {
                    seriesKey,
                    seriesInstanceUID: descriptor.seriesInstanceUID,
                    stackIndex: descriptor.stackIndex,
                    stackCount: descriptor.stackCount,
                    studyInstanceUID: studyKey,
                    seriesNumber: descriptor.seriesNumber,
                    seriesDescription: descriptor.seriesDescription,
                    bodyPartExamined: descriptor.bodyPartExamined,
                    modality: descriptor.modality,
                    laterality: descriptor.laterality,
                    protocolName: descriptor.protocolName,
                    label: study.labels[seriesKey] || 'Series',
                    slices,
                    sliceCount: slices.length,
                    middleIndex,
                    middleSlice: slices[middleIndex],
                    thumbnailUrl: null,
                    fileHashes
                };
            })
            .filter(Boolean);

        study.tags = deriveStudyTags(study);
    });

    const studyKeys = Object.keys(grouped).sort((a, b) => {
        const aDate = grouped[a].studyDate || '';
        const bDate = grouped[b].studyDate || '';
        if (aDate !== bDate) return aDate.localeCompare(bDate);
        return grouped[a].description.localeCompare(grouped[b].description);
    });

    return studyKeys.map((key) => ({
        ...grouped[key],
        key
    }));
},

    // buildSaveMetadata produces the JSON body for POST /api/imaging/metadata.
    // Files and thumbnails are uploaded separately in their own requests so the
    // server already has them in S3 — and ent_file rows already inserted —
    // by the time this payload is sent. The caller supplies:
    //   - `thumbnailUrlBySeriesKey`: map of series key -> fully-qualified S3
    //     URL returned from POST /api/imaging/thumbnail
    buildSaveMetadata: (study, thumbnailUrlBySeriesKey = {}) => {
        return {
            studyInstanceUID: study.studyInstanceUID,
            description: study.description || null,
            studyDate: study.studyDate || null,
            tags: study.tags || [],
            series: study.series.map((seriesEntry) => {
                const slices = seriesEntry.slices;
                const first = slices[0];
                const seriesUID = first.seriesInstanceUID || null;
                const seriesKey = dicomHelpers.getSeriesKey(seriesEntry);
                const thumbnailUrl = seriesKey
                    ? thumbnailUrlBySeriesKey[seriesKey] || ''
                    : '';

                return {
                    seriesInstanceUID: seriesUID,
                    seriesNumber: first.seriesNumber || 0,
                    seriesDescription: first.seriesDescription || null,
                    bodyPartExamined: first.bodyPartExamined || null,
                    modality: first.modality || null,
                    sliceCount: slices.length,
                    fileHashes: seriesEntry.fileHashes || [],
                    thumbnailUrl,
                };
            })
        };
    }
}